import { formatDateTime } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import type { PhoneCommunication } from '@seen/core/models/communications/PhoneCommunication'
import { format, parse, parseISO } from 'date-fns'
import type { JSX } from 'react'
import { Card } from '#src/components/core/Card.tsx'

interface PhoneCallTranscriptProps {
  phoneCall: PhoneCommunication
}

export function PhoneCallTranscript({
  phoneCall,
}: PhoneCallTranscriptProps): JSX.Element {
  const transcript = phoneCall.transcript

  const callSummary = getCallSummary(phoneCall)

  if (!transcript || transcript.length === 0) {
    return (
      <Card className="space-y-4 p-4">
        <h3 className="text-lg font-semibold">Transcript</h3>
        <p className="text-muted-foreground">No transcript available</p>
        {callSummary && (
          <p className="text-sm text-muted-foreground">{callSummary}</p>
        )}
      </Card>
    )
  }

  return (
    <Card className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Transcript</h3>
        {callSummary && (
          <p className="text-sm text-muted-foreground">{callSummary}</p>
        )}
      </div>

      <div className="space-y-4">
        {transcript.map((entry, index) => (
          <div key={index} className="flex gap-4">
            <div className="w-16 shrink-0 text-sm text-muted-foreground">
              {formatTimestamp(entry.startTime)}
            </div>
            <div className="grow">
              <div className="font-medium">{entry.speaker}</div>
              <div className="text-sm">{entry.text}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

function formatTimestamp(timestamp: string): string {
  const time = parse(timestamp, 'HH:mm:ss.SSS', new Date())
  return format(time, 'mm:ss')
}

function getCallSummary(phoneCall: PhoneCommunication): string | null {
  if (!phoneCall.startTime || !phoneCall.endTime) {
    return null
  }

  const startTime = parseISO(phoneCall.startTime)

  const durationStr = phoneCall.duration ?? 'unknown duration'
  const callerName =
    referenceDisplay(phoneCall.caller) ?? phoneCall.callerNumber ?? 'Unknown'
  const receiverName =
    referenceDisplay(phoneCall.receiver) ??
    phoneCall.receiverNumber ??
    'Unknown'

  const direction = phoneCall.isOutbound ? 'outbound' : 'inbound'
  return `${direction} call (${durationStr}) from ${callerName} to ${receiverName} on ${formatDateTime(startTime)}`
}
